import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api, {
  clearAuthTokens,
  logoutSession,
  refreshSession,
  setAccessToken,
  setRefreshToken,
} from '../services/api';
import * as analytics from '../utils/analytics';

const AuthContext = createContext();
const CACHED_USER_KEY = 'cached_user';

function readCachedUser() {
  try {
    const raw = localStorage.getItem(CACHED_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch (_) {
    /* ignore corrupt cache */
  }
  return null;
}

function writeCachedUser(userData) {
  try {
    if (userData && typeof userData === 'object' && !Array.isArray(userData)) {
      localStorage.setItem(CACHED_USER_KEY, JSON.stringify(userData));
      return;
    }
    localStorage.removeItem(CACHED_USER_KEY);
  } catch (_) {
    /* ignore storage failures */
  }
}

function clearCachedUser() {
  try {
    localStorage.removeItem(CACHED_USER_KEY);
  } catch (_) {
    /* ignore */
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const initialToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const initialCachedUser = initialToken ? readCachedUser() : null;

  const [user, setUser] = useState(initialCachedUser);
  // With a cached profile, show the app immediately; verify token in the background.
  const [loading, setLoading] = useState(!(initialToken && initialCachedUser));
  const [token, setToken] = useState(initialToken);
  const [proSetups, setProSetups] = useState(null);
  const [proSetupsLoading, setProSetupsLoading] = useState(false);

  const prefetchProSetups = useCallback(async () => {
    setProSetupsLoading(true);
    try {
      const { getMyProSetups } = await import('../services/api');
      const res = await getMyProSetups();
      setProSetups(res.data || []);
    } catch (e) {
      console.warn('Failed to prefetch Pro setups:', e?.message);
      setProSetups([]);
    } finally {
      setProSetupsLoading(false);
    }
  }, []);

  const applyAuthSession = useCallback((accessToken, refreshToken, userData) => {
    setToken(accessToken);
    if (userData && typeof userData === 'object' && !Array.isArray(userData)) {
      setUser(userData);
      writeCachedUser(userData);
      const userId = userData.id ?? userData.user_id;
      if (userId != null) analytics.setUserId(userId);
      
      // Pre-fetch Pro setups in background when user logs in
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => prefetchProSetups());
      } else {
        setTimeout(prefetchProSetups, 1000);
      }
    } else {
      console.error('Invalid user payload in auth response:', userData);
      setUser(null);
      clearCachedUser();
    }
    setAccessToken(accessToken);
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }
  }, [prefetchProSetups]);

  const logout = useCallback(async ({ server = true } = {}) => {
    const existingRefreshToken = localStorage.getItem('refresh_token');
    if (server && existingRefreshToken) {
      try {
        await logoutSession(existingRefreshToken);
      } catch (error) {
        console.warn('Logout endpoint failed:', error?.message);
      }
    }
    setToken(null);
    setUser(null);
    setProSetups(null);
    clearCachedUser();
    clearAuthTokens();
    delete api.defaults.headers.common.Authorization;
    import('../utils/dataCache').then((mod) => {
      mod.clearAllDataCaches?.();
    }).catch(() => {});
    import('../components/ActivityList').then((mod) => {
      mod.clearActivitiesCache?.();
    }).catch(() => {});
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/api/auth/me');
      if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        setUser(response.data);
        writeCachedUser(response.data);
      } else {
        console.error('Invalid user data format:', response.data);
        setUser(null);
        clearCachedUser();
      }
    } catch (error) {
      const statusCode = error?.response?.status;
      if (statusCode === 401) {
        const existingRefreshToken = localStorage.getItem('refresh_token');
        if (!existingRefreshToken) {
          await logout({ server: false });
        } else {
          try {
            const refreshResponse = await refreshSession(existingRefreshToken);
            const data = refreshResponse?.data || {};
            applyAuthSession(data.access_token, data.refresh_token, data.user);
          } catch (refreshError) {
            await logout({ server: false });
          }
        }
      } else {
        // Keep current session during transient network/backend failures.
        console.warn('Auth bootstrap skipped due to transient error:', error?.message);
      }
    } finally {
      setLoading(false);
    }
  }, [applyAuthSession, logout]);

  useEffect(() => {
    const initializeSession = async () => {
      if (token) {
        api.defaults.headers.common.Authorization = `Bearer ${token}`;
        // Background verify — UI already shown when cached_user exists
        await fetchUser();
        
        // Pre-fetch Pro setups when returning with an existing token
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(() => prefetchProSetups());
        } else {
          setTimeout(prefetchProSetups, 1000);
        }
        
        return;
      }

      const existingRefreshToken = localStorage.getItem('refresh_token');
      if (!existingRefreshToken) {
        setLoading(false);
        return;
      }

      try {
        const refreshResponse = await refreshSession(existingRefreshToken);
        const data = refreshResponse?.data || {};
        applyAuthSession(data.access_token, data.refresh_token, data.user);
      } catch (error) {
        clearAuthTokens();
        clearCachedUser();
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeSession();
  }, [applyAuthSession, fetchUser, prefetchProSetups, token]);

  const login = async (phone, password) => {
    const response = await api.post('/api/auth/login', {
      phone,
      password
    });

    const { access_token, refresh_token, user: userData } = response.data;
    applyAuthSession(access_token, refresh_token, userData);
    analytics.event('login', { method: 'password' });
    return { success: true, mustChangePassword: (userData && userData.must_change_password) || false };
  };

  const signup = async (phone, password, name, flatNumber, email = '') => {
    const response = await api.post('/api/auth/signup', {
      phone,
      password,
      name,
      flat_number: flatNumber,
      email: email || null
    });

    const { access_token, refresh_token, user: userData } = response.data;
    applyAuthSession(access_token, refresh_token, userData);
    analytics.event('sign_up', { method: 'password' });
    return { success: true };
  };

  const exchangeFirebaseToken = async (idToken) => {
    const response = await api.post('/api/auth/firebase/exchange', {
      id_token: idToken,
    });
    const data = response.data || {};

    if (data.needs_profile_completion) {
      return {
        success: true,
        needsProfileCompletion: true,
        signupToken: data.signup_token,
        phone: data.phone,
      };
    }

    applyAuthSession(data.access_token, data.refresh_token, data.user);
    return { success: true, needsProfileCompletion: false };
  };

  /** Local login: exchange phone (and optional secret) for session when backend allows local login. */
  const exchangeLocalPhone = async (phone, localSecret = '') => {
    const response = await api.post('/api/auth/firebase/exchange', {
      dev_phone: phone,
      ...(localSecret ? { dev_secret: localSecret } : {}),
    });
    const data = response.data || {};

    if (data.needs_profile_completion) {
      return {
        success: true,
        needsProfileCompletion: true,
        signupToken: data.signup_token,
        phone: data.phone,
      };
    }

    applyAuthSession(data.access_token, data.refresh_token, data.user);
    return { success: true, needsProfileCompletion: false };
  };

  const completeFirebaseProfile = async ({ signupToken, name, flatNumber }) => {
    const response = await api.post('/api/auth/firebase/complete-profile', {
      signup_token: signupToken,
      name,
      flat_number: flatNumber,
    });
    const data = response.data || {};
    applyAuthSession(data.access_token, data.refresh_token, data.user);
    return { success: true };
  };

  const changePassword = async (currentPassword, newPassword) => {
    await api.post('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return { success: true };
  };

  const updateProfile = async (profileData) => {
    const response = await api.put('/api/auth/profile', profileData);
    setUser(response.data);
    writeCachedUser(response.data);
    return { success: true };
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await api.get('/api/auth/me');
        setUser(response.data);
        writeCachedUser(response.data);
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
  };

  const value = {
    user,
    token,
    proSetups,
    proSetupsLoading,
    prefetchProSetups,
    login,
    signup,
    exchangeFirebaseToken,
    exchangeLocalPhone,
    completeFirebaseProfile,
    changePassword,
    updateProfile,
    logout,
    refreshUser,
    loading,
    isAuthenticated: !!user || !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
